import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, TextField, InputAdornment, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LockResetIcon from "@mui/icons-material/LockReset";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { fetchData, putData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import { filterRows } from "../../utils/tableUtils";

const FALLBACK_USERS = [
  { id: "1", fullName: "Mrs. Kavitha Rajan", email: "kavitha@cts.org", role: "teacher" },
  { id: "2", fullName: "Admin User", email: "admin@cts.org", role: "admin" },
  { id: "3", fullName: "Aravind Kumar", email: "aravind@cts.org", role: "student" },
];

const ROLE_CONFIG = {
  admin:   { color: "#244C77", bg: "#EAF1F8" },
  teacher: { color: "#16a34a", bg: "#f0fdf4" },
  student: { color: "#2563eb", bg: "#eff6ff" },
};

const AdminPasswordReset = () => {
  const [users, setUsers] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [resetDialog, setResetDialog] = useState({ open: false, user: null, password: "", showPass: false });
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    try {
      const res = await fetchData("admin/password-reset/users");
      if (res?.data) {
        const data = Array.isArray(res.data) ? res.data : [];
        setUsers(data.length > 0 ? data : FALLBACK_USERS);
      } else setUsers(FALLBACK_USERS);
    } catch (_) { setUsers(FALLBACK_USERS); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filterRows(users, filterText);

  const openReset = (user) => setResetDialog({ open: true, user, password: "", showPass: false });

  const handleReset = async () => {
    const { user, password } = resetDialog;
    if (!password || password.length < 6) {
      setToast({ open: true, severity: "error", message: "Password must be at least 6 characters." });
      return;
    }
    try {
      await putData(`admin/password-reset/reset/${user.id}`, { newPassword: password });
      setToast({ open: true, severity: "success", message: `Password reset for ${user.fullName}!` });
      setResetDialog({ open: false, user: null, password: "", showPass: false });
    } catch (err) {
      setToast({ open: true, severity: "error", message: err?.response?.data?.message || "Failed to reset password." });
    }
  };

  return (
    <div style={{ textAlign: "left", padding: "1rem" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />

      <p style={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", margin: "0 0 8px" }}>
        Pages / Admin Password Reset
      </p>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <LockResetIcon sx={{ color: "#244C77", fontSize: "28px" }} />
        <Box>
          <Typography sx={{ fontWeight: "bold", fontSize: "22px", color: "#111827" }}>Password Reset</Typography>
          <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>Reset passwords for any user in the system</Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField size="small" fullWidth placeholder="Search by name, email, role..."
          value={filterText} onChange={e => setFilterText(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#8F95B2", fontSize: "18px" }} /></InputAdornment> }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" }, maxWidth: 400 }}
        />
      </Box>

      <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#1E293B" }}>
              <TableRow>
                {["#", "Full Name", "Email", "Role", "Actions"].map(h => (
                  <TableCell key={h} sx={{ color: "#fff", fontWeight: 600, fontSize: "13px" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={5} sx={{ textAlign: "center", py: 6, color: "#8F95B2" }}>No users found</TableCell></TableRow>
              ) : filtered.map((user, idx) => {
                const rc = ROLE_CONFIG[user.role] || ROLE_CONFIG.student;
                return (
                  <TableRow key={user.id || idx} sx={{ "&:hover": { backgroundColor: "#f9fafb" } }}>
                    <TableCell sx={{ fontSize: "13px", color: "#6B7280" }}>{idx + 1}</TableCell>
                    <TableCell sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{user.fullName}</TableCell>
                    <TableCell sx={{ fontSize: "13px", color: "#374151" }}>{user.email}</TableCell>
                    <TableCell>
                      <Chip label={user.role} size="small"
                        sx={{ backgroundColor: rc.bg, color: rc.color, fontWeight: 600, fontSize: "11px", textTransform: "capitalize" }} />
                    </TableCell>
                    <TableCell>
                      <Button size="small" startIcon={<LockResetIcon />} onClick={() => openReset(user)}
                        sx={{ background: "linear-gradient(90deg, #4A7BA8, #244C77)", color: "#fff", fontSize: "12px", textTransform: "none", borderRadius: "8px", px: 2 }}>
                        Reset
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Reset Dialog */}
      <Dialog open={resetDialog.open} onClose={() => setResetDialog({ ...resetDialog, open: false })} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "17px" }}>Reset Password</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography sx={{ fontSize: "14px", color: "#374151", mb: 2 }}>
              Resetting password for: <strong>{resetDialog.user?.fullName}</strong>
            </Typography>
            <TextField
              fullWidth size="small"
              type={resetDialog.showPass ? "text" : "password"}
              placeholder="New Password (min 6 characters)"
              value={resetDialog.password}
              onChange={e => setResetDialog({ ...resetDialog, password: e.target.value })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setResetDialog(prev => ({ ...prev, showPass: !prev.showPass }))}>
                      {resetDialog.showPass ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setResetDialog({ ...resetDialog, open: false })} fullWidth
            sx={{ border: "1.5px solid #D1D5DB", borderRadius: "10px", textTransform: "none", color: "#374151" }}>
            Cancel
          </Button>
          <Button onClick={handleReset} fullWidth variant="contained"
            sx={{ borderRadius: "10px", backgroundColor: "#2D3A5C", textTransform: "none", fontWeight: 600, boxShadow: "none" }}>
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminPasswordReset;
